
import { Component, Watch, Mixins } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import { MatrixClip, MatrixClipContents } from "@/store/site-monitor-cameras/types";
import { EventRecord } from "@/store/site-monitor/types";
import { AuditService, MediaFile } from "@/services/auditService";
import MatrixSidebarMixin from "./MatrixSidebarMixin";

const SiteMonitor = namespace("siteMonitor");
const SMCameras = namespace("siteMonitorCameras");

@Component
export default class ClipsList extends Mixins(MatrixSidebarMixin) {
	@Getter("getFeature") private getFeature: (featureNames: string[]) => boolean;

	@SiteMonitor.Getter("getAuditService") private auditService: AuditService;
	@SiteMonitor.Getter("getSelectedEventRecord") private selectedEventRecord: EventRecord;

	@SMCameras.Getter("getMediaMatrixIsNew") private mediaMatrixIsNew: boolean;
	@SMCameras.Getter("getDeviceControllerClips") private clips: MatrixClipContents[];

	@SMCameras.Mutation private setDeviceControllerClips: (clips: MatrixClipContents[]) => void;
	@SMCameras.Mutation private setPushContents: ({ index, newContents }) => void;
	@SMCameras.Mutation private setAwaitingClip: (clip: MatrixClip) => void;
	@SMCameras.Mutation private highlightCell: (index: number) => void;
	@SMCameras.Mutation private unhighlightCell: () => void;

	private filter: string = "";

	/**
	 * Used as a lock to prevent more than one timeout being created to poll
	 * for, and update, clips.
	 */
	private getClipsLock: boolean = false;
	private getClipsTimeout: NodeJS.Timeout | null = null;

	private clipsFailed: boolean = false;
	private clipsFailedStatus: string = "";
	private noClipsAvailable: boolean = false;

	/**
	 * Returns true if we should list all clips, not just the ones for the currently selected event
	 * record.
	 */
	private get allClipsEnabled() {
		return this.getFeature(["Alarms", "SiteMonitor", "ShowAllClips"]);
	}

	/**
	 * Returns the list of clips, filtered by the users search text.
	 */
	private get filteredClips() {
		if (this.filter == null || this.filter == "") return this.clips;

		const filter = this.filter.toLowerCase();
		return this.clips.filter(c => c.DeviceTitle.toLowerCase().includes(filter));
	}

	/**
	 * Returns a map of the current clip indices in the matrix, indexed by the file identifier.
	 */
	private get matrixClips(): { [fileIdentifier: string]: number } {
		return Object.entries(this.allMatrixContents).reduce((prev, [index, value]) => {
			let clip = value?.clip?.clip;
			if (clip) {
				prev[clip.FileIdentifier] = parseInt(index);
			}
			return prev;
		}, {});
	}

	/**
	 * Clears out any running timeouts.
	 */
	public beforeDestroy() {
		this.reset();
	}

	/**
	 * Resets the state of the component, and clears any running timeouts.
	 */
	private reset() {
		this.clipsFailed = false;
		this.clipsFailedStatus = "";
		this.noClipsAvailable = false;
		this.getClipsLock = false;

		clearTimeout(this.getClipsTimeout);
		this.getClipsTimeout = null;
	}

	/**
	 * Clears out any running timeouts if we switch events.
	 */
	@Watch("eventId")
	private onEventChanged() {
		this.reset();
	}

	/**
	 * Watches for when the selected event record changes. Resets the state of the clip list and
	 * begins fetching the media list for the new event record.
	 */
	@Watch("selectedEventRecord", { immediate: true })
	private async onSelectedEventRecordChanged(value: EventRecord, oldValue: EventRecord) {
		if (!value) {
			return;
		}

		// If we're already polling for the record, return.
		if (value.eventRecordID == oldValue?.eventRecordID && this.getClipsTimeout) {
			return;
		}

		this.getClipsLock = false;
		clearTimeout(this.getClipsTimeout);

		if (value) {
			await this.getMediaList(value.eventRecordID, value.attachedCameras);
		}
	}

	/**
	 * Watches for when the matrix is new state changes. If we're new (value is true, oldValue is false),
	 * it triggers fetching the media list.
	 */
	@Watch("mediaMatrixIsNew")
	private async onMediaMatrixIsNewChanged(value: boolean, oldValue: boolean) {
		if (value && !oldValue) {
			// Start polling for clips if we've got an event record, and we aren't already polling.
			if (this.selectedEventRecord && !this.getClipsTimeout) {
				await this.getMediaList(
					this.selectedEventRecord.eventRecordID,
					this.selectedEventRecord.attachedCameras
				);
			}
		}
	}

	/**
	 * Attempts to update the media list.
	 */
	private async getMediaList(eventRecordID: number, cameras: any[]) {
		if (this.eventId == 0) {
			return;
		}

		this.reset();

		if (!this.getSession("2")) {
			// If we haven't got a session ID for audit, fetch one before we start polling
			// for the media list.
			await this.updateSession({
				resourceId: 2,
				eventId: this.eventId
			});
		}

		await this.getClips(eventRecordID, cameras, true);
	}

	/**
	 * Gets the list of clips from the audit service, for the given event record ID.
	 */
	private async getClips(eventRecordID: number, cameras: any[], addUpdatedClips: boolean) {
		// If another timeout happens to be already polling for clips, return.
		if (this.getClipsLock) {
			return;
		}

		this.getClipsLock = true;

		let auth = this.getSession("2");
		let clipsTimeoutPeriod = 5_000;

		try {
			// Get the list of clips for the currently selected event record.
			const { data } = await this.auditService.mediaList(auth, eventRecordID, this.allClipsEnabled);

			// If we've switched event records, or left the event, and this timeout wasn't cleared in time, reset the lock and return.
			if (eventRecordID != this.selectedEventRecord.eventRecordID || this.eventId == 0) {
				this.getClipsLock = false;
				return;
			}

			// If we've got some placeholder clips in our data, we'll poll again in ~2 seconds, instead of the default 5.
			if (data.some(clip => clip.FileIdentifier.toLowerCase().includes("placeholder"))) {
				clipsTimeoutPeriod = 2_000;
			}

			const clips = data || [];

			// Clear any previous errors
			this.clipsFailed = false;
			this.clipsFailedStatus = "";

			// Filter out all clips that don't have a file identifier, device information, are of the RAW DATA type
			const filteredClips = clips.filter(
				clip =>
					clip.MediaType != "RAW DATA" &&
					clip.FileIdentifier &&
					(clip.DeviceId || clip.DeviceTitle || clip.StartTime)
			);

			this.noClipsAvailable = filteredClips.length == 0;

			// Get the list of clips that have been added to the event since our last getClips call.
			let newClips = filteredClips.filter(clip => !this.clips.some(c => c.FileIdentifier == clip.FileIdentifier));

			// Update the indices for our clips if they're already in the matrix, or replace placeholders if
			// any are available. The result will be our array of clips, along with their indices, which we can
			// display in the list.
			const deviceControllerClips = this.updateClipIndicesAndReplacePlaceholders(filteredClips);
			this.setDeviceControllerClips(deviceControllerClips);

			// Filter our new clips list, and remove any clips that have been added to the matrix (specifically.
			// this check removes any clips that have overwritten placeholders in the last step).
			newClips = newClips.filter(clip => {
				let index = deviceControllerClips?.find(c => c.FileIdentifier == clip.FileIdentifier)?.index;
				return !index;
			});

			// Emit a clipsRetrieved event, and pass along the list of new clips. This event will make the
			// DeviceController attempt to populate the matrix with the new clips, and the cameras, if
			// space if available. (This is what is responsible for the initial set of cameras/clips you see
			// when you pick up an event for the first time).
			this.$emit("clipsRetrieved", { eventRecordID, cameras, newClips });

			/**
			 * TODO: The ClipsList component !!!should not!!! be the component responsible for triggering the
			 * initial camera setup in the matrix, and especially not through an event called 'clipsRetrieved'.
			 * We need to look at how DeviceController and its children are structured, as nothing is
			 * simple or straightforward, and none of the code is properly commented.
			 *
			 * Ideally, DeviceController (for example) would be responsible for populating the cameras when
			 * the selectedEventRecord changes. ClipsList should just get clips and list them.
			 */
		} catch (err) {
			if (
				err?.response?.status == 500 &&
				err?.response?.data?.toLowerCase().indexOf("system.io.directorynotfoundexception") > -1
			) {
				this.noClipsAvailable = true;
				this.clipsFailed = false;
				this.clipsFailedStatus = "";
			} else {
				this.noClipsAvailable = false;
				this.clipsFailed = true;

				if (err?.response?.statusText) {
					this.clipsFailedStatus = err?.response?.statusText ?? "";
				} else {
					let auditProtocol = this.auditService.protocol;
					let currentProtocol = location.protocol;

					if (currentProtocol.toLowerCase() == "https:" && auditProtocol.toLowerCase() == "http:") {
						this.clipsFailedStatus = "Unable to access clips due to security restrictions";
					} else {
						this.clipsFailedStatus = "Failed to retrieve clips";
					}
				}
			}

			this.setDeviceControllerClips([]);
			this.$emit("clipsRetrieved", {
				eventRecordID,
				cameras,
				newClips: []
			});
		} finally {
			this.getClipsLock = false;
		}

		this.getClipsTimeout = setTimeout(() => {
			this.getClips(eventRecordID, cameras, addUpdatedClips);
		}, clipsTimeoutPeriod);
	}

	/**
	 * Takes the list of clips and returns a new list with each clip assigned an index, or null. If a clip is
	 * already in the matrix, it will be assigned it's current index. If the clip isn't currently in the matrix,
	 * but a placeholder slot is available, it will be given the index of (and overwrite) the placeholder.
	 */
	private updateClipIndicesAndReplacePlaceholders(clips: MediaFile[]): MatrixClipContents[] {
		// Get a list of clip placeholders currently in the matrix.
		const placeholders = Object.keys(this.matrixClips).filter(clip => clip.startsWith("Placeholder"));
		const matrixClips: MatrixClipContents[] = clips.map(clip => {
			const currentMatrixIndex = this.matrixClips[clip.FileIdentifier];

			if (currentMatrixIndex) {
				// If the clip is already in the MM, we can just use it's current index.
				return {
					...clip,
					index: currentMatrixIndex
				};
			} else if (placeholders.length > 0) {
				// The clip isn't in the matrix, but we have a placeholder available, take the first one and get
				// it's index, using our matrixClips map.
				const placeholderSlot = placeholders.shift();
				const placeholderIndex = this.matrixClips[placeholderSlot];

				// Push the clip into the matrix, at the placeholder index.
				this.setPushContents({
					index: placeholderIndex,
					newContents: {
						clip: {
							eventRecordID: clip.EventRecordId,
							clip: {
								FileIdentifier: clip.FileIdentifier
							}
						}
					}
				});

				// Return the clip with it's new index
				return {
					...clip,
					index: placeholderIndex
				};
			} else {
				// Clip isn't in the MM, and there are no placeholder slots available. Set index to null.
				return {
					...clip,
					index: null
				};
			}
		});

		return matrixClips;
	}

	/**
	 * Returns the class(es) to use for a given clip item in the list.
	 */
	public getClipListItemStyle(clip: any) {
		if (clip.FileSizeError) return "bg-danger text-white";
		if (clip.Error != null) return "bg-warning text-white";
		if (this.isSelectedClip(clip)) return "active text-white";
		return "";
	}

	/**
	 * Returns true if there are any errors in the clip, or if it's below the minimum file size.
	 */
	public isClipError(clip: MatrixClipContents) {
		if (clip.Error != null || clip.FileSizeError) return true;
		return false;
	}

	/**
	 * Returns true if the clip has been selected (is in the matrix).
	 */
	public isSelectedClip(clip: MatrixClipContents) {
		return !!clip && clip.index !== null && !isNaN(clip.index);
	}

	/**
	 * Returns the class to use for the clip matrix icon.
	 */
	private clipIconColor(clip: MatrixClipContents, i: number, j: number) {
		if ((i - 1) * this.layoutHorizontal + j === clip.index) return "redCameraIndicator";
		else if (clip.FileSizeError) return "bg-danger";
		else if (clip.Error != null) return "bg-warning";
		else return "greyCameraIndicator";
	}

	/**
	 * Selects a clip to be placed into the matrix.
	 */
	private selectClip(clip: MatrixClipContents) {
		if (this.isController) {
			this.setAwaitingClip({
				eventRecordID: clip.EventRecordId,
				clip: clip
			});
		}
	}
}
